import { supabase } from '@/integrations/supabase/client';
import {
  fetchContactInteractions as fetchInteractionsFromService,
  createContactInteraction as createInteractionFromService,
} from './interactionsService';

export type RelationshipHealth = {
  id?: string;
  contactId: string;
  score: number; // 0-100
  sentiment?: 'positive' | 'neutral' | 'negative';
  lastInteractionDate?: string;
  nextInteractionDate?: string;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  health_score?: number;
};

export const fetchContactRelationshipHealth = async (contactId: string) => {
  // Try to fetch from the relationship_health table first
  const { data: relationshipData, error: relationshipError } = await supabase
    .from('relationship_health')
    .select('*')
    .eq('contact_id', contactId)
    .maybeSingle();

  if (!relationshipError && relationshipData) {
    // Calculate sentiment based on health score
    let sentiment = 'neutral';
    if (relationshipData.health_score >= 70) {
      sentiment = 'positive';
    } else if (relationshipData.health_score <= 30) {
      sentiment = 'negative';
    }

    return {
      ...relationshipData,
      sentiment,
    };
  }

  // If no relationship record exists, calculate from activities
  const { data: activities, error } = await supabase
    .from('interactions')
    .select('*')
    .eq('entity_id', contactId)
    .eq('entity_type', 'contact')
    .order('interaction_date', { ascending: false });

  if (error) throw error;

  // Calculate a simple relationship health score based on recency and frequency
  let score = 50; // Default neutral score
  let sentiment = 'neutral';
  let lastInteractionDate = null;

  if (activities && activities.length > 0) {
    // More recent activities improve the score
    const mostRecent = activities[0];
    lastInteractionDate = mostRecent.interaction_date;

    const now = new Date();
    const lastDate = new Date(mostRecent.interaction_date);
    const daysSinceLastInteraction = Math.floor(
      (now.getTime() - lastDate.getTime()) / (1000 * 3600 * 24)
    );

    if (daysSinceLastInteraction < 7) {
      score += 20;
    } else if (daysSinceLastInteraction < 30) {
      score += 10;
    } else if (daysSinceLastInteraction > 90) {
      score -= 20;
    }

    // More interactions improve the score
    if (activities.length >= 5) {
      score += 20;
    } else if (activities.length >= 3) {
      score += 10;
    }

    // Sentiment analysis (simplified)
    if (score >= 70) {
      sentiment = 'positive';
    } else if (score <= 30) {
      sentiment = 'negative';
    }
  }

  const healthScore = Math.min(100, Math.max(0, score));

  // Create a new health record if one doesn't exist
  if (!relationshipData) {
    const { data: newRecord, error: insertError } = await supabase
      .from('relationship_health')
      .insert({
        contact_id: contactId,
        health_score: healthScore,
        last_assessment_date: new Date().toISOString(),
      })
      .select()
      .maybeSingle();

    if (insertError) {
      // Condition check only
    }
  }

  return {
    contactId,
    health_score: healthScore,
    sentiment,
    last_assessment_date: new Date().toISOString(),
    lastInteractionDate,
    opportunities_count: 0,
    activities: activities?.length || 0,
  };
};

export const updateRelationshipHealth = async (data: any) => {
  const { data: updated, error } = await supabase
    .from('relationship_health')
    .update(data)
    .eq('id', data.id)
    .select()
    .maybeSingle();

  if (error) throw error;
  return updated;
};

// These functions are now provided by interactionsService.ts
export const fetchContactInteractions = fetchInteractionsFromService;
export const createContactInteraction = createInteractionFromService;

export const calculateOrganizationHealthMetrics = async (organizationId: string) => {
  // Get all contacts for the organization
  const { data: contacts, error: contactsError } = await supabase
    .from('contacts')
    .select('id')
    .eq('organization_id', organizationId);

  if (contactsError) throw contactsError;

  if (!contacts || contacts.length === 0) {
    return {
      organizationId,
      averageScore: 50,
      contactsAnalyzed: 0,
      lastInteractionDate: null,
    };
  }

  // Get relationship health metrics for all contacts
  const contactIds = contacts.map((c) => c.id);
  const { data: healthRecords, error: healthError } = await supabase
    .from('relationship_health')
    .select('*')
    .in('contact_id', contactIds);

  if (healthError) throw healthError;

  // Calculate average health score
  let totalScore = 0;
  let contactsWithHealth = 0;

  if (healthRecords && healthRecords.length > 0) {
    contactsWithHealth = healthRecords.length;
    totalScore = healthRecords.reduce((sum, record) => sum + (record.health_score || 0), 0);
  }

  const averageScore = contactsWithHealth > 0 ? totalScore / contactsWithHealth : 50;

  // Get latest interaction
  const { data: latestInteraction, error: interactionError } = await supabase
    .from('interactions')
    .select('interaction_date')
    .in('entity_id', contactIds)
    .eq('entity_type', 'contact')
    .order('interaction_date', { ascending: false })
    .limit(1);

  if (interactionError) throw interactionError;

  return {
    organizationId,
    averageScore: Math.round(averageScore),
    contactsAnalyzed: contacts.length,
    contactsWithHealthData: contactsWithHealth,
    lastInteractionDate:
      latestInteraction && latestInteraction.length > 0
        ? latestInteraction[0].interaction_date
        : null,
  };
};
